'use client';

import { faChevronDown } from '@fortawesome/pro-light-svg-icons';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@pickleballinc/react-ui';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@pickleballinc/ui/components/ui/dropdown-menu';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';

import { CURRENT_SEED_TABS, PRO_WORLD_RANKING_TABS } from '@/modules/rankings/constants';
import { PlayerFiltersInterface } from '@/types/ranking.types';

interface TournamentTypeButtonsProps {
  filters: PlayerFiltersInterface;
}

export function TournamentTypeButtons({ filters }: TournamentTypeButtonsProps) {
  const router = useRouter();
  const searchParams = useSearchParams();

  const handleClick = (type: string, gender: string) => {
    const newSearchParams = new URLSearchParams(searchParams || '');
    newSearchParams.set('type', type);
    newSearchParams.set('gender', gender);
    newSearchParams.set('page', '1');
    router.replace(`?${newSearchParams.toString()}`);
  };

  const isProWorldRanking = filters.rankingType === 'proWorldRanking';
  const tabs = isProWorldRanking ? PRO_WORLD_RANKING_TABS : CURRENT_SEED_TABS;

  const isActive = (tab: { type: string; gender: string }) => {
    if (isProWorldRanking) {
      return filters.gender === tab.gender;
    }
    if (tab.type === '3') {
      return filters.type === '3' && filters.gender === tab.gender;
    }
    return filters.type === tab.type;
  };

  const activeTab = tabs.find((tab) => isActive(tab)) ?? tabs[0];

  return (
    <div className='flex w-full justify-center overflow-auto bg-white md:pb-6'>
      <div className='flex w-full max-w-screen-xl flex-col gap-2 px-3 sm:px-4 2xl:px-0 py-2'>
        <div className='flex flex-row items-center justify-between gap-2'>
          <div className='hidden gap-2 lg:flex'>
            {tabs.map((tab) => (
              <Button
                key={`desktop-${tab.label}`}
                variant={isActive(tab) ? 'secondary-color' : 'secondary'}
                className={isActive(tab) ? 'bg-purple-50' : 'bg-white'}
                size='lg'
                onClick={() => handleClick(tab.type, tab.gender)}
              >
                {tab.label}
              </Button>
            ))}
          </div>
          <div className='flex w-full lg:hidden'>
            <DropdownMenu>
              <DropdownMenuTrigger className='outline-none w-full'>
                <div className='relative flex items-center justify-between px-[14px] py-[10px] text-base text-gray-900 font-medium rounded-lg border border-gray-300 bg-white shadow-xs hover:cursor-pointer'>
                  {activeTab?.label}
                  <FontAwesomeIcon icon={faChevronDown} size='sm' />
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent className='flex flex-col text-base text-gray-900 bg-white font-medium w-[--radix-dropdown-menu-trigger-width]'>
                {tabs
                  .filter((tab) => !isActive(tab))
                  .map((tab) => (
                    <DropdownMenuItem
                      key={`mobile-dropdown-${tab.label}`}
                      onClick={() => handleClick(tab.type, tab.gender)}
                      className='flex text-base cursor-pointer !w-full'
                    >
                      {tab.label}
                    </DropdownMenuItem>
                  ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        <Link
          href='https://www.ppatour.com/about/how-it-works/'
          target='_blank'
          rel='noopener noreferrer'
          className='ml-auto flex items-center gap-1.5 text-sm font-medium underline text-purple-700'
        >
          How does this work?
          <FontAwesomeIcon icon={faArrowUpRightFromSquare} className='text-sm' />
        </Link>
      </div>
    </div>
  );
}
